import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MondelezDataService } from './mondelez-data.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { throwError, Observable, of } from 'rxjs';

@Injectable()
export class MondelezService {
  constructor(private http: HttpClient, private mondelezDataService: MondelezDataService, private spinner: NgxSpinnerService) {}

  fetchData<T>(url: string, data: any, replaceWithZero, method: 'GET' | 'POST' = 'POST'): Observable<T> {
    this.spinner.show();

    let request: Observable<any>;

    if (method === 'POST') {
      request = this.http.post(url, data, { responseType: 'text' });
    } else {
      request = this.http.get(url, { responseType: 'text' });
    }

    return request.pipe(
      map((response) => {
        let cleanedResponse;
        if (replaceWithZero) cleanedResponse = this.replaceInvalidValues(response);
        else cleanedResponse = this.replaceInvalidValuesWithInfinity(response);

        let parsedData: T;
        try {
          parsedData = JSON.parse(cleanedResponse);
        } catch (error) {
          console.error('Error parsing response:', cleanedResponse);
          throw new Error('Invalid JSON response');
        }
        return parsedData;
      }),
      catchError((error) => {
        console.error('API error:', error);
        return throwError(() => new Error('API error occurred'));
      }),
      finalize(() => {
        this.spinner.hide();
      })
    );
  }

  replaceInvalidValues(response: string): string {
    return response.replace(/Infinity/g, '0').replace(/NaN/g, '0');
  }

  replaceInvalidValuesWithInfinity(response: string): string {
    return response
      .replace(/-Infinity/g, '"-Infinity"')
      .replace(/(?<!-)Infinity/g, '"Infinity"')
      .replace(/NaN/g, '0');
  }

  public fetchFilterData(data) {
    const url = `${environment.serverUrl}/simulator/filter-data`;
    this.spinner.show();
    return this.http.post(url, data).pipe(
      catchError((error) => {
        this.spinner.hide();
        return of(null);
      })
    );
  }

  // public fetchPopUpFilterData(data) {
  //   const url = `${environment.serverUrl}/simulator/filter-data`;
  //   this.spinner.show();
  //   return this.http.post(url, data);
  // }

  // public fetchGlobalFilterData(data) {
  //   const url = `${environment.serverUrl}/simulator/filter-data`;
  //   this.spinner.show();
  //   return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  // }

  public fetchMapViewFilterData() {
    const url = `${environment.serverUrl}/simulator/map_view_master`;
    this.spinner.show();
    return this.http.get(url).pipe(finalize(() => this.spinner.hide()));
  }

  public getScenarioData() {
    const url = `${environment.serverUrl}/simulator/scenario-list`;
    this.spinner.show();
    return this.http.get(url).pipe(
      tap(
        (result: any) => {
          this.spinner.hide();
          if (result['response'] === 'SUCCESS') {
            this.mondelezDataService.updateScenarioList(result['scenario']);
            // this.mondelezDataService.updateCurrentScenario(result['scenario'][result['scenario'].length - 1]);
          } else {
            this.mondelezDataService.updateScenarioList([]);
          }
        },
        (error) => {
          this.spinner.hide();
        }
      )
    );
  }

  getGlobalParamsData(data: any) {
    const url = `${environment.serverUrl}/simulator/global-parameters`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  solveSimulationCapex(data: any) {
    const url = `${environment.serverUrl}/simulator/solve-simulation-capex`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getInsightsData(data: any) {
    this.spinner.show();
    const url = `${environment.serverUrl}/simulator/get-insights`;
    return this.http.post(url, data).pipe(finalize(() => setTimeout(() => this.spinner.hide(), 500)));
  }

  getMapViewDialogData(data: any) {
    const url = `${environment.serverUrl}/simulator/get-node-info`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getSimulationTableData(data: any) {
    const url = `${environment.serverUrl}/simulator/simulation`;
    return this.fetchData<any>(url, data, true, 'POST');
  }

  getSimulationParameters(data: any) {
    const url = `${environment.serverUrl}/simulator/simulation-parameters`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  solveSimulation(param: any) {
    const url = `${environment.serverUrl}/simulator/save-simulation-params`;
    this.spinner.show();
    return this.http.post(url, param).pipe(finalize(() => this.spinner.hide()));
  }

  applySimulation(param: any) {
    const url = `${environment.serverUrl}/simulator/apply-simulation-params`;
    this.spinner.show();
    return this.http.post(url, param).pipe(finalize(() => this.spinner.hide()));
  }

  solveLogisticsSimulation(param: any) {
    const url = `${environment.serverUrl}/simulator/calculate-inventory-throughput`;
    this.spinner.show();
    return this.http.post(url, param).pipe(finalize(() => this.spinner.hide()));
  }

  createScenario(scenarioData) {
    const url = `${environment.serverUrl}/simulator/create-scenario`;
    this.spinner.show();
    return this.http.post(url, scenarioData).pipe(finalize(() => this.spinner.hide()));
  }

  updateScenario(scenarioData) {
    const url = `${environment.serverUrl}/simulator/update-scenario`;
    this.spinner.show();
    return this.http.post(url, scenarioData).pipe(finalize(() => this.spinner.hide()));
  }

  deleteScenario(scenario) {
    const url = `${environment.serverUrl}/simulator/delete-scenario`;
    this.spinner.show();
    return this.http.post(url, scenario).pipe(finalize(() => this.spinner.hide()));
  }

  getScenarioComparisonData(data: any) {
    const url = `${environment.serverUrl}/simulator/scenario-comparison`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getStepSummary(data: any) {
    const url = `${environment.serverUrl}/simulator/get-steps-summary`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getKpiDashboardData(data: any) {
    const url = `${environment.serverUrl}/simulator/kpi-dashboard`;
    return this.http.post(url, data);
  }

  getKpiDashboardOperationalGraphData(data: any) {
    const url = `${environment.serverUrl}/simulator/kpi-dashboard-operational-graph`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getKpiDashboardOperationalOtherCapexData(data: any) {
    const url = `${environment.serverUrl}/simulator/kpi-dashboard-other-capex`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getMapViewData(data: any) {
    const url = `${environment.serverUrl}/simulator/map-view`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  public getAssignmentData(data: any) {
    const url = `${environment.serverUrl}/simulator/get-resource-assignment-details`;
    this.spinner.show();
    return this.http.post(url, data).pipe(
      finalize(() => this.spinner.hide()),
      tap(
        (result: any) => {
          if (result['response'] === 'SUCCESS') {
            this.mondelezDataService.updateAssignmentData({
              assigned_data: result['assigned_data'],
              unassigned_data: result['unassigned_data'],
            });
          } else {
            this.mondelezDataService.updateAssignmentData({
              assigned_data: [],
              unassigned_data: [],
            });
          }
        },
        (error) => {
          this.mondelezDataService.updateAssignmentData({
            assigned_data: [],
            unassigned_data: [],
          });
        }
      )
    );
  }

  saveAssignmentData(assignmentData) {
    const url = `${environment.serverUrl}/simulator/save-resource-assignment-details`;
    this.spinner.show();
    return this.http.post(url, assignmentData).pipe(finalize(() => this.spinner.hide()));
  }

  getMasterPlananAlysisInsightData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-analysis-insights`;
    return this.fetchData<any>(url, data, false, 'POST');
  }

  getMasterPlanComparisonInsightData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-comparison-insights`;
    return this.fetchData<any>(url, data, false, 'POST');
  }

  getMasterPlanAnalysisSummaryGraphData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-analysis-summary-view-graph`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getMasterPlanComparisonSummaryGraphData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-comparison-summary-view-graph`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getMasterPlanAnalysisSummaryTableData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-analysis-summary-view`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getMasterPlanComparisonSummaryTableData(data) {
    const url = `${environment.serverUrl}/simulator/master-plan-comparison-summary-view`;
    this.spinner.show();
    return this.http.post(url, data).pipe(finalize(() => this.spinner.hide()));
  }

  getRepackLinesTableData(data: any) {
    const url = `${environment.serverUrl}/simulator/mups-repack-lines`;
    return this.fetchData<any>(url, data, true, 'POST');
  }

  getMupsLineAnalysisTableData(data: any) {
    const url = `${environment.serverUrl}/simulator/mups-line-analysis`;
    return this.fetchData<any>(url, data, true, 'POST');
  }

  downloadMasterData(payload) {
    const url = `${environment.serverUrl}/simulator/row-master-data`;
    return this.http.post(url, payload);
  }
}
